/**
 * Read-only queries over the renderer's `LayoutNode` tree, shared by the
 * popover menus (src/renderer.ts) and the Phase 8 swipe actions
 * (src/interaction.ts): the text and symbol a `button` row shows, and the
 * `tint` set on it.
 */

import type { LayoutNode } from './layout';
import type { Color } from './protocol';

/** All `text` in a subtree, joined with spaces (a `Label`'s title, a button's text). */
export function textOf(node: LayoutNode): string {
  const parts: string[] = [];
  const visit = (n: LayoutNode): void => {
    if (n.kind === 'text' && typeof n.props['text'] === 'string') parts.push(n.props['text']);
    n.children.forEach(visit);
  };
  visit(node);
  return parts.join(' ').trim();
}

/** The first SF Symbol (`image` with `systemName`) in a subtree, or null. */
export function symbolOf(node: LayoutNode): string | null {
  if (node.kind === 'image' && typeof node.props['systemName'] === 'string') return node.props['systemName'];
  for (const child of node.children) {
    const found = symbolOf(child);
    if (found) return found;
  }
  return null;
}

/** The `style` record of a `styled` node (empty for anything else). */
export function styleRecordOf(node: LayoutNode): Record<string, unknown> {
  const style = node.props['style'];
  return typeof style === 'object' && style !== null ? (style as Record<string, unknown>) : {};
}

/**
 * Phase 8: the `tint` a swipe action button carries, looked for on the
 * `styled` wrappers above the button (`.tint()` on the `Button`) and on the
 * single-child chain below it (a tinted label). `null` when none.
 */
export function tintOf(node: LayoutNode): Color | null {
  let current: LayoutNode | undefined = node;
  while (current) {
    if (current.kind === 'styled') {
      const tint = styleRecordOf(current)['tint'];
      if (typeof tint === 'object' && tint !== null) return tint as Color;
    }
    current = current.children.length === 1 ? current.children[0] : undefined;
  }
  return null;
}

/** The element under a chain of `styled` wrappers. */
export function unwrapStyledNode(node: LayoutNode): LayoutNode {
  let current = node;
  while (current.kind === 'styled' && current.children.length === 1) current = current.children[0] as LayoutNode;
  return current;
}
